/**
 * BooksMediaServicesSection - Shows relevant service providers for Books & Media subcategories
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Star, MapPin, ChevronRight, Building, 
  BookOpen, Newspaper, GraduationCap, BookImage,
  Disc, Download, Gem, Pencil, Loader2,
  Phone, Mail, ExternalLink, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map category slugs to service types
const CATEGORY_TO_SERVICE_TYPE = {
  'books': 'bookstore',
  'magazines': 'magazine-store',
  'educational-media': 'educational-center',
  'comics-graphic-novels': 'comic-store',
  'cds-dvds': 'media-store',
  'digital-media': 'digital-store',
  'collectibles': 'collectibles-dealer',
  'stationery': 'stationery-store',
};

// Category icons
const CATEGORY_ICONS = {
  'books': BookOpen,
  'magazines': Newspaper,
  'educational-media': GraduationCap,
  'comics-graphic-novels': BookImage,
  'cds-dvds': Disc,
  'digital-media': Download,
  'collectibles': Gem,
  'stationery': Pencil,
};

// Service type colors
const SERVICE_COLORS = {
  'books': 'from-blue-500 to-indigo-600',
  'magazines': 'from-pink-500 to-rose-600',
  'educational-media': 'from-purple-500 to-violet-600',
  'comics-graphic-novels': 'from-amber-500 to-orange-600',
  'cds-dvds': 'from-cyan-500 to-blue-600',
  'digital-media': 'from-red-500 to-rose-600',
  'collectibles': 'from-emerald-500 to-teal-600',
  'stationery': 'from-gray-500 to-slate-600',
};

// Section titles
const SECTION_TITLES = {
  'books': 'Bookstores & Libraries',
  'magazines': 'Magazine Retailers',
  'educational-media': 'Educational Suppliers',
  'comics-graphic-novels': 'Comic Book Shops',
  'cds-dvds': 'Music & Media Stores',
  'digital-media': 'Digital Content Providers',
  'collectibles': 'Collectibles Dealers',
  'stationery': 'Stationery Suppliers',
};

// Check if category is a books-media category
export function isBooksMediaCategory(slug) {
  return Object.keys(CATEGORY_TO_SERVICE_TYPE).includes(slug);
}

function ProviderCard({ provider, currencySymbol, categorySlug }) {
  const Icon = CATEGORY_ICONS[categorySlug] || Building;
  const gradientClass = SERVICE_COLORS[categorySlug] || 'from-gray-500 to-gray-600';
  
  return (
    <Card 
      className="group hover:shadow-lg transition-all border border-gray-200 hover:border-blue-300"
      data-testid={`books-media-provider-${provider.id}`}
    >
      <div className="relative h-32 overflow-hidden rounded-t-lg">
        {provider.images?.[0] ? (
          <img 
            src={provider.images[0]} 
            alt={provider.provider_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${gradientClass} flex items-center justify-center`}>
            <Icon className="w-12 h-12 text-white opacity-70" />
          </div>
        )}
        
        {provider.verified && (
          <Badge className="absolute top-2 left-2 bg-green-500">Verified</Badge>
        )}
        
        {provider.rating && (
          <div className="absolute top-2 right-2 bg-white/90 px-2 py-1 rounded-full flex items-center gap-1">
            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
            <span className="text-xs font-medium">{provider.rating.toFixed(1)}</span>
          </div>
        )}
      </div>
      
      <CardContent className="p-4">
        <h4 className="font-semibold line-clamp-1 group-hover:text-blue-600 transition-colors">
          {provider.provider_name}
        </h4>
        
        <p className="text-sm text-gray-500 line-clamp-2 mt-1 min-h-[2.5rem]">
          {provider.description}
        </p>
        
        <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
          <MapPin className="w-3 h-3" />
          <span>{provider.location}</span>
        </div>
        
        {provider.services_offered && provider.services_offered.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {provider.services_offered.slice(0, 3).map((service, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {service}
              </Badge>
            ))}
            {provider.services_offered.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{provider.services_offered.length - 3}
              </Badge>
            )}
          </div>
        )}
        
        <div className="flex gap-2 mt-4">
          {provider.phone && (
            <Button size="sm" variant="outline" className="flex-1" asChild>
              <a href={`tel:${provider.phone}`}>
                <Phone className="w-3 h-3 mr-1" />
                Call
              </a>
            </Button>
          )}
          {provider.website && (
            <Button size="sm" variant="outline" className="flex-1" asChild>
              <a href={provider.website} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-3 h-3 mr-1" />
                Visit
              </a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function BooksMediaServicesSection({ categorySlug, country = 'LK', maxProviders = 4 }) {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  const sectionTitle = SECTION_TITLES[categorySlug] || 'Service Providers';
  const Icon = CATEGORY_ICONS[categorySlug] || Building;
  const gradientClass = SERVICE_COLORS[categorySlug] || 'from-gray-500 to-gray-600';
  
  useEffect(() => {
    const fetchProviders = async () => {
      if (!categorySlug || !isBooksMediaCategory(categorySlug)) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const serviceType = CATEGORY_TO_SERVICE_TYPE[categorySlug];
        const response = await fetch(
          `${API_URL}/api/books-media/providers?country=${country}&category=${categorySlug}&limit=${maxProviders}`
        );
        
        if (!response.ok) throw new Error('Failed to fetch providers');
        
        const data = await response.json();
        setProviders(data.providers || []);
      } catch (err) {
        console.error('Error fetching providers:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProviders();
  }, [categorySlug, country, maxProviders]);
  
  // Don't render if not a books-media category
  if (!isBooksMediaCategory(categorySlug)) {
    return null;
  }
  
  if (loading) {
    return (
      <div className="py-8">
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading services...</span>
        </div>
      </div>
    );
  }
  
  if (error || providers.length === 0) {
    return null;
  }
  
  return (
    <section className="py-8" data-testid="books-media-services-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-gradient-to-br ${gradientClass}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{sectionTitle}</h2>
            <p className="text-sm text-gray-500">Trusted providers in your area</p>
          </div>
        </div>
        
        <Button 
          variant="ghost" 
          className="text-blue-600 hover:text-blue-700"
          onClick={() => navigate(`/books-media?category=${categorySlug}`)}
        >
          View All
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {providers.map(provider => (
          <ProviderCard 
            key={provider.id} 
            provider={provider} 
            currencySymbol={currencySymbol}
            categorySlug={categorySlug}
          />
        ))}
      </div>
    </section>
  );
}
